import { useCustomFieldsQuery } from '../../../asset/services/queries';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Fetch from '../../util/_fetch';
import { Collapse, notification } from 'antd';
import Button from '../../Button';
import SubmitButton from '../../SubmitButton';
import { ExportAssetType } from '../../../asset/util/useAddPermissions';
import {
  AnyAssetSubType,
  ConnectionTypes,
  createUrl,
  dangerButtonColors,
  defaultResponseHandler,
  Flex,
  formatMs,
  FormikFormAsset,
  FormikFormConnectionKey,
  FormikNewFormValues,
  getCustomFieldsSchema,
  Loading,
  NewAsset,
  openNotificationWithIcon,
  useAssetConfig,
  useAssetTranslation,
} from '@mss/mss-component-library';
import { Formik } from 'formik';
import { useQueries } from '@tanstack/react-query';
import {
  assetModel,
  exportAssetTypeHeaders,
  exportAssetTypeSubTypeKey,
  exportAssetTypeToCategory,
  fullAssetName,
  mapAssetsInitialTouched,
} from './util';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { array, lazy, object } from 'yup';
import { Form } from 'formik-antd/lib/form';
import { assetSubTypeInformation } from './config';
import { Check, X } from 'react-feather';
import restartImportModal from './RestartImportModal';
import TestAllButton from './TestAllButton';
import SettingsForm from '../Shared/SettingsForm';
import Connection from '../Shared/Connection';
import AssetCollectors from '../Add/AssetCollectors';
import { sanitizeAssetForPost } from '../utils/sanitize';
import { assetTypeAPItoURI } from '../utils/conversions';
import { PreFillSettings } from './PreFillSettings';

type ImportOnboardingProps<AssetType extends NewAsset> = {
  initialValues: FormikNewFormValues<AssetType>;
  currentStepValue: string;
  currentStep: number;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  restart?: () => void;
  type: ExportAssetType;
  enableSubmit: boolean;
  onValidationFailed?: (failed: boolean) => void;
};

const exportTypeToCollectorUrlPrefix = (category: ExportAssetType) => {
  switch (category) {
    case 'asset_management':
    case 'analyzer_management':
    case 'virtualization':
    case 'control':
      return 'applications';
    default:
      return 'assets';
  }
};

function ImportOnboarding<AssetType extends NewAsset>({
  initialValues,
  type,
  currentStepValue,
  currentStep,
  restart,
  enableSubmit,
  goToNextStep,
  goToPreviousStep,
  onValidationFailed
}: ImportOnboardingProps<AssetType>) {

  const customFields = useCustomFieldsQuery(assetModel[type]);
  const { t } = useAssetTranslation();
  const { config } = useAssetConfig();
  const assetSubType = exportAssetTypeSubTypeKey<AssetType>(type);
  const history = useHistory();
  const { url } = useRouteMatch();
  const isDiscoveryPage = url.includes('/discovery');
  const initialOpenPanels = useMemo(() => {
    return assetSubType.options.reduce(
      (acc, option) => {
        acc[option] = initialValues.assets
          .filter(x => String(x.values[assetSubType.key]) === option)
          .map(x => x.values.tempId);
        return acc;
      },
      {} as Record<string, number[]>
    );
  }, [initialValues.assets, assetSubType]);

  const [openPanels, setOpenPanels] =
    useState<Record<string, number[]>>(initialOpenPanels);

  const [selectedSeries] = useState<
    { id: number; value: string; subType: AnyAssetSubType }[]
  >(
    initialValues.assets.map(x => ({
      id: x.values.tempId,
      value: '',
      // @ts-ignore
      subType: x.values[assetSubType.key] as AnyAssetSubType,
    }))
  );

  const updateOpenPanels = (key: string) => (panels: string | string[]) =>
    setOpenPanels(prev => ({
      ...prev,
      [key]: [...(Array.isArray(panels) ? panels : [panels]).map(Number)],
    }));

  const hasFilteredCollectors = type === 'network';
  const queries = hasFilteredCollectors
    ? selectedSeries.map(({ subType, value }) => ({
      queryKey: ['collectors', type, subType, value],
      enabled: !!value,
      cacheTime: formatMs({ hours: 1 }),
      staleTime: formatMs({ hours: 1 }),
      queryFn: async () =>
        (
          await defaultResponseHandler<any>({
            t,
            request: Fetch.get(
              `/api/${exportTypeToCollectorUrlPrefix(type)}/${type.replace(
                '_',
                ''
              )}/${subType}/collectors/?series_id=${value}`
            ),
          })
        ).data ?? null,
    }))
    : assetSubType.options.map(subtype => ({
      queryKey: ['collectors', type, subtype],
      cacheTime: formatMs({ hours: 1 }),
      staleTime: formatMs({ hours: 1 }),
      queryFn: async () =>
        (
          await defaultResponseHandler<any>({
            t,
            request: Fetch.get(
              `/api/${exportTypeToCollectorUrlPrefix(type)}/${type.replace(
                '_',
                ''
              )}/${subtype.replace('windows_', '')}/collectors/`
            ),
          })
        ).data ?? null,
    }));

  useQueries({
    queries: type === 'field' ? [] : queries,
  });

  const settingsKeys = [
    ...exportAssetTypeHeaders(type).map(x => x.key),
    'custom_field_values',
  ];

  const validationSchema = object().shape({
    assets: array().of(
      object({
        values: lazy((asset: any) => {
          const _assetSubType = asset[assetSubType.key] as AnyAssetSubType;
          const schema =
            config.assets[_assetSubType]?.form?.configure.schema ?? object();
          const customFieldsSchema = object(
            getCustomFieldsSchema(t, customFields.data ?? [])
          );
          return object()
            .concat(schema ?? object())
            .concat(customFieldsSchema);
        }),
      })
    ),
  });

  const successNotification = (responses: any[]) => {
    openNotificationWithIcon(
      notification.success,
      t(
        `((fullAssetName)) ${responses.length > 1 ? 'were' : 'was'
        } added successfully.`,
        {
          fullAssetName:
            fullAssetName(type) + (responses.length > 1 ? 's' : ''),
        }
      ),
      ''
    );
  };

  const goToList = () =>
    history.push(url.replace('import', '').replace('discovery/add', ''));

  const handleCustomDiscoverySubmit = async (
    values: FormikNewFormValues<AssetType>
  ) => {
    try {
      if (!config.discovery?.submitOptions)
        throw new Error('No submit options provided');
      const assetData = values.assets.map(x => x.values);
      const data =
        config.discovery.submitOptions.dataParser(assetData) ?? assetData;
      const resp = await Fetch.post(config.discovery.submitOptions.url, {
        body: JSON.stringify(data),
      });
      if (!resp.ok) throw new Error('Failed to submit data');
      successNotification(data);
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Failed to add assets'),
        ''
      );
    } finally {
      goToList();
    }
  };
  const handleSubmit = async (values: FormikNewFormValues<AssetType>) => {
    try {
      const responses = await Promise.all<Response>(
        values.assets.map(({ values: asset }) => {
          const subType = asset[assetSubType.key] as unknown as AnyAssetSubType;
          const matchingConfig = config.assets[subType];
          const requiredInputs = Object.values(
            matchingConfig?.form.connect ?? {}
          ).flatMap(
            config =>
              config.inputs?.filter(i => i.required)?.map(x => x.fieldName) ??
              []
          );

          return Fetch.post(
            createUrl({
              assetType: assetTypeAPItoURI[config.listView.assetType],
              assetSubType: subType,
            }),
            {
              body: JSON.stringify({
                ...requiredInputs.reduce((acc, x) => ({ ...acc, [x]: x }), {}),
                ...sanitizeAssetForPost(
                  matchingConfig?.form.submitDataParser?.(asset) || asset
                ),
              }),
            }
          );
        })
      );

      const results = await Promise.all(responses.map(r => r.json()));

      if (responses.some(x => !x.ok)) {
        // @ts-ignore
        throw new Error('Import (partially) failed', { cause: results });
      }
      successNotification(responses);
    } catch (e: any) {
      if (e?.cause && Array.isArray(e.cause)) {
        const { error, success } = e.cause.reduce(
          (acc: any, x: any) => {
            if (x.id) {
              acc.success.push(x);
            } else {
              acc.error.push(x);
            }
            return acc;
          },
          {
            error: [],
            success: [],
          }
        );

        openNotificationWithIcon(
          notification.error,
          t(
            success.length
              ? 'asset:Onboarding assets partially failed'
              : 'asset:Onboarding assets failed'
          ),
          <>
            <div>
              <X size={16} className="text-danger mr-1" />
              {t(
                `((errors)) out of ((total)) ((type)) could not be added.`,
                {
                  errors: error.length,
                  total: e.cause.length,
                  type: exportAssetTypeToCategory(type),
                }
              )}
            </div>
            {success.length && (
              <div>
                <Check size={16} className="text-success mr-1" />
                {success.length} out of {e.cause.length} assets were added
                successfully.
              </div>
            )}
          </>
        );
      }
    } finally {
      goToList();
    }
  };

  if (initialValues.assets.length === 0 || !customFields.isSuccess) {
    return <Loading />;
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnMount
        initialTouched={{
          assets: initialValues.assets.map(
            mapAssetsInitialTouched(customFields.data) as any
          ) as any,
        }}
        onSubmit={
          isDiscoveryPage && config.discovery?.submitOptions
            ? handleCustomDiscoverySubmit
            : handleSubmit
        }
      >
        {({ values, errors }) => {

          const selectedAssetMap = useMemo(() => {
            const map = values.assets.reduce((acc, assetItem) => {
              const tempId = assetItem.values.tempId;
              acc[tempId] = assetItem.values;
              return acc;
            }, {} as Record<number, any>);
            return map;
          }, [values.assets]);

          const allErrors = Array.from(
            new Set([
              ...(Array.isArray(errors?.assets) ? errors.assets : [])
                .map((x: any) => Object.keys(x?.values ?? {}))
                .flat(),
            ])
          );
          const hasAnySettingsError = Array.from(allErrors).some(x =>
            settingsKeys.includes(x)
          );

          const hasAnyConnectionError = Array.from(allErrors).some(x =>
            exportAssetTypeSubTypeKey(type)
              .options.map(x => assetSubTypeInformation(x).connectionKeys)
              .flat()
              .includes(x)
          );

          const selectedConnectionTypes = Array.from(
            new Set(
              values.assets.map(
                (x: FormikFormAsset<any>) =>
                  `test_${x.values.connection_type ?? 'direct'
                  }` as FormikFormConnectionKey
              )
            )
          );
          const hasAnyFailedTest = values.assets.some(x =>
            Object.keys(x).some(key =>
              selectedConnectionTypes.includes(key as FormikFormConnectionKey)
                ? Object.values(
                  x[key as FormikFormConnectionKey].result.status
                ).some(result => result === false)
                : false
            )
          );
          const hasAnyTestNotRan = values.assets.some(
            asset =>
              ['snmp', 'direct', 'telnet', 'ssh'].filter(
                ctype =>
                  asset[`test_${ctype}` as FormikFormConnectionKey]?.result
                    .status.port == null
              ).length === 4
          );

          const hasAnyRunningTest = values.assets.some(x =>
            ['snmp', 'direct', 'telnet', 'ssh'].some(
              // @ts-ignore
              y => x[`test_${y}` as keyof FormikFormAsset<any>]?.loading
            )
          );

          const validationFailed = values.assets.some((asset: any) => {
            const { version, community, security_level: secLevel, security_username: user, auth_password: authPwd, privacy_password: privPwd } = asset.values;
            if ([1, 2].includes(version)) return !community?.trim();
            if (version === 3) {
              if (!user?.trim()) return true;
              if (secLevel === 'no_auth_or_privacy') return false;
              if (secLevel === 'auth_without_privacy') return !authPwd?.trim();
              if (secLevel === 'auth_with_privacy') return !authPwd?.trim() || !privPwd?.trim();
              return true;
            }
            return true;
          });
          useEffect(() => {
            onValidationFailed?.(validationFailed);
          }, [validationFailed, onValidationFailed]);

          
          const assetsGroupedBySubType = values.assets.reduce(
            (acc, asset) => {
              // @ts-ignore
              acc[asset.values[assetSubType.key]] = [
                // @ts-ignore
                ...(acc[asset.values[assetSubType.key]] ?? []),
                asset,
              ];
              return acc;
            },
            {} as Record<
              string,
              ImportOnboardingProps<AssetType>['initialValues']['assets']
            >
          );
          const [preFillAssetStatus, setPreFillAssetStatus] = useState<Record<number, boolean>>({});
          const handlePreFillStatusChange = useCallback((isComplete: boolean) => {
            if (type === 'field') {
              const selectedAsset = initialValues.assets?.[0]?.values?.tempId || 0;
              setPreFillAssetStatus(prev => ({
                ...prev,
                [selectedAsset]: isComplete
              }));
            }
          }, [type, initialValues.assets]);

          const isRreFillCompleted = useMemo(() => {
            if (type !== 'field' || currentStepValue !== 'preFill') return true;
            const selectedAssetID = initialValues.assets.map(asset => asset.values.tempId);
            return selectedAssetID.some(assetId => preFillAssetStatus[assetId] === true) || selectedAssetID.length === 0;
          }, [type, currentStepValue, initialValues.assets, preFillAssetStatus]);

          return (
            <Form>
              {currentStepValue === 'preFill' && type === 'field' && (
                <PreFillSettings
                  assetMap={selectedAssetMap}
                  onStatusChange={handlePreFillStatusChange}
                />
              )}
              {currentStepValue !== 'preFill' && Object.entries(assetsGroupedBySubType)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, assets]) => {
                  const overrideAssetParams = {
                    assetSubType: key as AnyAssetSubType,
                  };
                  const matchingConfig = config.assets[key as AnyAssetSubType]!;
                  const groupTempIds = assets.map(
                    ({ values: asset }) => asset.tempId
                  );
                  const hasOpenPanels = openPanels[key].length;
                  const hasSingleTestType =
                    Object.keys(matchingConfig.form.connect).length === 1;
                  const disabledConnection =
                    Object.keys(matchingConfig.form.connect).length === 0;
                  return (
                    <Fragment key={key}>
                      <Flex
                        v={hasSingleTestType ? 'center' : 'start'}
                        h="between"
                        className="mb-3"
                      >
                        <Flex v="end">
                          <h4 className={'mb-0 mr-2 inline-block'}>
                            {t(
                              assetSubType.labels[key as AnyAssetSubType] ?? key
                            )}
                          </h4>
                          <span
                            onClick={() => {
                              hasOpenPanels
                                ? updateOpenPanels(key)([])
                                : updateOpenPanels(key)(
                                  groupTempIds.map(String)
                                );
                            }}
                            className="text-xs text-primary hover:underline cursor-pointer fort-normal"
                          >
                            {t(hasOpenPanels ? 'Collapse all' : 'Expand all')}
                          </span>
                        </Flex>
                        <span>
                          {currentStepValue === 'connection' &&
                            !disabledConnection &&
                            hasSingleTestType && (
                              <TestAllButton
                                testConnectionType={
                                  Object.keys(
                                    matchingConfig.form.connect
                                  )[0] as ConnectionTypes
                                }
                                overrideAssetParams={overrideAssetParams}
                              />
                            )}
                        </span>
                      </Flex>
                      {currentStepValue === 'connection' &&
                        !disabledConnection &&
                        !hasSingleTestType && (
                          <Flex v="center" className="mb-4">
                            <h6 className="mr-4 mb-0">
                              {t('Test all ((assetSubType)) connections', {
                                assetSubType: t(key),
                              })}
                              :
                            </h6>
                            <div>
                              <ul className={'m-0'}>
                                {Object.keys(matchingConfig.form.connect).map(
                                  (key: string) => (
                                    <li className="inline" key={key}>
                                      <TestAllButton
                                        multiple
                                        testConnectionType={
                                          key as ConnectionTypes
                                        }
                                        overrideAssetParams={
                                          overrideAssetParams
                                        }
                                      />
                                    </li>
                                  )
                                )}
                              </ul>
                            </div>
                          </Flex>
                        )}
                      <Collapse
                        className="mb-8"
                        onChange={updateOpenPanels(key)}
                        activeKey={openPanels[key]}
                        defaultActiveKey={openPanels[key]}
                      >
                        {assets.map(({ values: asset }) => {
                          return (
                            <Collapse.Panel
                              key={asset.tempId}
                              header={
                                type === 'field' && !asset.name
                                  ? ''
                                  : (asset.name ||
                                    t(`asset:Unnamed ((category)) ((index))`, {
                                      category: exportAssetTypeToCategory(type),
                                      index: asset.tempId,
                                    }))
                              }
                            >
                              {currentStepValue === 'settings' && (
                                <SettingsForm
                                  index={asset.tempId}
                                  overrideAssetParams={overrideAssetParams}
                                />
                              )}
                              {currentStepValue === 'connection' && (
                                <Connection
                                  index={asset.tempId}
                                  overrideAssetParams={overrideAssetParams}
                                  disableNotification
                                />
                              )}
                              {currentStepValue === 'collectors' && (
                                <AssetCollectors
                                  index={asset.tempId}
                                  overrideAssetParams={overrideAssetParams}
                                />
                              )}
                            </Collapse.Panel>
                          );
                        })}
                      </Collapse>
                    </Fragment>
                  );
                })}

              <Flex h="between">
                <Button
                  onClick={goToPreviousStep}
                  disabled={
                    currentStep === 0 ||
                    (currentStepValue === 'connection' && hasAnyRunningTest)
                  }
                >
                  {t('Previous')}
                </Button>
                <span>
                  {!!restart && (
                    <Button
                      className="mr-2"
                      variant="primary"
                      ghost
                      disabled={
                        currentStepValue === 'connection' && hasAnyRunningTest
                      }
                      colorOverrides={dangerButtonColors}
                      onClick={() => restartImportModal({ restart, t })}
                    >
                      {t('Restart import')}
                    </Button>
                  )}
                  {currentStepValue === 'connection' &&
                    (hasAnyTestNotRan || hasAnyFailedTest) && (
                      <Button
                        ghost
                        onClick={() => {
                          setOpenPanels(initialOpenPanels);
                          goToNextStep();
                        }}
                        colorOverrides={dangerButtonColors}
                        className="mr-2"
                        disabled={hasAnyRunningTest || validationFailed}
                        data-ta="saveCredentialsFailedTestButton"
                      >
                        {t(
                          hasAnyTestNotRan
                            ? 'Continue without completed tests'
                            : 'Continue despite failed tests'
                        )}
                      </Button>
                    )}
                  {!enableSubmit && (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => {
                        setOpenPanels(initialOpenPanels);
                        goToNextStep();
                      }}
                      disabled={
                        (currentStepValue === 'settings' &&
                          hasAnySettingsError) ||
                        (currentStepValue === 'connection' &&
                          (hasAnyConnectionError ||
                            hasAnyTestNotRan ||
                            hasAnyFailedTest) ||
                          (currentStepValue === 'preFill' && !isRreFillCompleted)
                        )
                      }
                    >
                      {t('Next')}
                    </Button>
                  )}
                  {enableSubmit && (
                    <SubmitButton disabled={Object.keys(errors).length}>
                      {t('Submit')}
                    </SubmitButton>
                  )}
                </span>
              </Flex>
              {Object.keys(errors).length > 0 && enableSubmit && (
                <Flex h="end">
                  <span className={'text-xs text-danger mt-2'}>
                    {t('Not all required fields are filled in')}
                  </span>
                </Flex>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

export default ImportOnboarding;
